import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * One paged, cursor-driven read, scoped at the READ to every dimension it was fetched under
 * (MAR-325). The one implementation behind Knowledge's two index lists (`useIndex`) and the
 * live feed (`LiveFeed`), which used to carry two copies of the same guards.
 *
 * ── The key ─────────────────────────────────────────────────────────────────────────────
 *
 * `key` names EVERY dimension the page was fetched under — workspace, account, and which
 * list (index kind, feed source) — built with `scopeKey`. The stored page carries its key,
 * and a stored page whose key is not the live one is not returned: React renders once with
 * the new props and the old state before any effect runs, and that frame would paint one
 * workspace's (or one source's) rows under another's name. `null` means there is nothing to
 * read (no workspace), and nothing is claimed.
 *
 * ── The guards, each load-bearing ───────────────────────────────────────────────────────
 *
 * - **Newest first page wins** (`seq`). A slow read for a key the merchant has left must
 *   not land over the one in front of them.
 * - **`live`**: `loadMore` reads the state as it stands, never a render-old closure.
 * - **One extension in flight per (key, cursor)** (`pendingMore`). Two presses before the
 *   first re-render would otherwise both carry the same cursor and append the SAME page
 *   twice; `loadingMore` alone is only visible after a commit.
 * - **Cursor-matched append.** A page fetched for a list that has since moved (another
 *   key, or a cursor past this one) extends nothing.
 * - **A failed extension keeps the cursor** (`moreFailed`): what is loaded is still good,
 *   and clearing `hasMore` would take the retry away.
 *
 * ── `enabled`, and why a read is requested once per key ─────────────────────────────────
 *
 * The first page is fetched only while `enabled`, so a caller can defer a read until the
 * view that renders it is on screen. Once requested for a key it is not requested again
 * when `enabled` flips back and forth — that would throw away every page the merchant
 * loaded. A NEW key (another workspace, another source) is a new read.
 *
 * ⚠️ ONE slot: the hook holds the page for the current key only. Going A → B → A re-reads
 * A; a caller that unmounts (the feed off its view) re-reads on return.
 *
 * A first page that FAILED is not re-requested by itself — `retry()` is the way back: it
 * clears the failed slot (so the list reads as loading, never as the old failure) and asks
 * again. It does nothing on a page that loaded.
 */

export interface PagedState<T> {
  /** Every dimension this page was fetched under (`scopeKey`). */
  key: string
  items: T[]
  /** The server's cursor, or `null` when the list is exhausted. */
  cursor: unknown
  /** There is another page, because there is a cursor to fetch it with. */
  hasMore: boolean
  /** The FIRST page refused. Not an empty list. */
  failed: boolean
  loadingMore: boolean
  /** A `Load more` refused. What is loaded is still good; only the extension failed. */
  moreFailed: boolean
}

export type FetchPage<T> = (cursor: unknown) => Promise<{ items: T[]; cursor: unknown }>

export interface PagedRead<T> {
  /** `null` while the first page for THIS key is unread or in flight. */
  page: PagedState<T> | null
  loadMore: () => void
  /** Ask again for a first page that failed. A no-op otherwise. */
  retry: () => void
}

/**
 * A key naming every scope dimension. JSON, so no dimension's value can collide with a
 * separator (`a:b` + `c` vs `a` + `b:c`).
 */
export function scopeKey(...dims: (string | null | undefined)[]): string {
  return JSON.stringify(dims.map((d) => d ?? null))
}

export function usePagedRead<T>(key: string | null, fetchPage: FetchPage<T>, enabled = true): PagedRead<T> {
  const [state, setState] = useState<PagedState<T> | null>(null)

  const live = useRef<PagedState<T> | null>(null)
  // ⚠️ Declared BEFORE the first-page effect, so both are current when it runs.
  const fetchRef = useRef(fetchPage)
  useEffect(() => {
    live.current = state
    fetchRef.current = fetchPage
  })

  const seq = useRef(0)
  /** The key the first page was last requested for. */
  const requested = useRef<string | null>(null)
  const pendingMore = useRef<{ key: string; cursor: unknown } | null>(null)

  /** Fetch the first page for `k`; only the newest such request may write. */
  const readFirst = useCallback((k: string) => {
    requested.current = k
    const n = ++seq.current
    ;(async() => {
      let next: PagedState<T>
      try {
        const p = await fetchRef.current(null)
        next = { key: k, items: p.items, cursor: p.cursor, hasMore: !!p.cursor, failed: false, loadingMore: false, moreFailed: false }
      } catch {
        // ⚠️ `failed`, never an empty list: a read that refused and a list with nothing in
        // it are different facts.
        next = { key: k, items: [], cursor: null, hasMore: false, failed: true, loadingMore: false, moreFailed: false }
      }
      if (seq.current === n) setState(next)
    })()
  }, [])

  useEffect(() => {
    if (!key) {
      requested.current = null
      return
    }
    if (!enabled || requested.current === key) return
    readFirst(key)
  }, [key, enabled, readFirst])

  const retry = useCallback(() => {
    const snap = live.current
    if (!key || !snap || snap.key !== key || !snap.failed) return
    // Cleared first, so the list reads as loading — not as the failure being retried.
    setState(null)
    live.current = null
    readFirst(key)
  }, [key, readFirst])

  const loadMore = useCallback(() => {
    const snap = live.current
    if (!key || !snap || snap.key !== key || !snap.hasMore || snap.loadingMore) return
    const from = snap.cursor
    const pending = pendingMore.current
    if (pending && pending.key === key && pending.cursor === from) return
    const mine = { key, cursor: from }
    pendingMore.current = mine
    const same = (p: PagedState<T> | null): p is PagedState<T> => !!p && p.key === key && p.cursor === from
    setState((p) => (same(p) ? { ...p, loadingMore: true, moreFailed: false } : p))
    ;(async() => {
      try {
        const pg = await fetchRef.current(from)
        setState((p) =>
          same(p)
            ? { ...p, items: [...p.items, ...pg.items], cursor: pg.cursor, hasMore: !!pg.cursor, loadingMore: false, moreFailed: false }
            : p
        )
      } catch {
        setState((p) => (same(p) ? { ...p, loadingMore: false, moreFailed: true } : p))
      } finally {
        if (pendingMore.current === mine) pendingMore.current = null
      }
    })()
  }, [key])

  return { page: state && state.key === key ? state : null, loadMore, retry }
}
